import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { IconButton, Tooltip } from "@mui/material";
import {
  ChartsGrid,
  TableContainer,
  ChartTitle,
  StyledTable,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableCell,
} from "../styles";
import colors from "../../../styles/colors";
import { ErrorRecord } from "../../../types/Statistics2";

interface MergedErrorRecord extends ErrorRecord {
  source: string;
  docTypeDisplay: string;
}

interface TableProps {
  title: string;
  items: MergedErrorRecord[];
  emptyLabel: string;
  messageLabel: string;
  docTypeLabel: string;
  dateLabel: string;
  sourceLabel: string;
  viewFileLabel: string;
  actionsLabel: string;
  markHandledLabel: string;
  unmarkHandledLabel: string;
  onToggleHandled: (transactionId: string, handled: boolean) => void;
  // אי התאמה tab: add invoice sum / GRV sum / difference / GRV numbers columns
  showGrvColumns?: boolean;
}

const formatShekels = (value?: number | null) =>
  typeof value === "number"
    ? `₪${value.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const ErrorBreakdownTable: React.FC<TableProps> = ({
  title,
  items,
  emptyLabel,
  messageLabel,
  docTypeLabel,
  dateLabel,
  sourceLabel,
  viewFileLabel,
  actionsLabel,
  markHandledLabel,
  unmarkHandledLabel,
  onToggleHandled,
  showGrvColumns = false,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 10;
  const sortedItems = [...items].sort(
    (a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf()
  );
  const hasMore = sortedItems.length > INITIAL_LIMIT;
  const displayedItems = showAll ? sortedItems : sortedItems.slice(0, INITIAL_LIMIT);

  return (
    <TableContainer style={{ minHeight: 180, display: "flex", flexDirection: "column" }}>
      <ChartTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{title}</span>
        {items.length > 0 && (
          <span style={{ fontSize: "0.8rem", color: colors.theme.yaelGrey, fontWeight: 500 }}>
            {t("TOTAL_ERRORS_COUNT", { count: items.length }) || `${items.length} שגיאות`}
          </span>
        )}
      </ChartTitle>
      {items.length === 0 ? (
        <div
          style={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.theme.yaelGrey,
            fontSize: "0.9rem",
          }}
        >
          {emptyLabel}
        </div>
      ) : (
        <>
          <div style={{ overflowY: "auto", maxHeight: showAll ? 420 : "none", flex: 1 }}>
            <StyledTable style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <TableHeader style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#f7fafc" }}>
                <TableRow>
                  <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>{messageLabel}</TableHeaderCell>
                  <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>{docTypeLabel}</TableHeaderCell>
                  {showGrvColumns && (
                    <>
                      <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>
                        {t("GRV_INVOICE_SUM") || "סכום חשבונית"}
                      </TableHeaderCell>
                      <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>
                        {t("GRV_SUM") || "סכום תעודות קבלה"}
                      </TableHeaderCell>
                      <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>
                        {t("GRV_DIFFERENCE") || "הפרש"}
                      </TableHeaderCell>
                      <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>
                        {t("GRV_DOCNOS") || "תעודות קבלה"}
                      </TableHeaderCell>
                    </>
                  )}
                  <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>{sourceLabel}</TableHeaderCell>
                  <TableHeaderCell style={{ backgroundColor: "#f7fafc" }}>{dateLabel}</TableHeaderCell>
                  <TableHeaderCell style={{ backgroundColor: "#f7fafc", textAlign: "end" }}>{actionsLabel}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {displayedItems.map((item) => (
                  <TableRow key={`${item.source}-${item.id}`}>
                    <TableCell style={{ wordBreak: "break-word" }}>{item.message}</TableCell>
                    <TableCell>{item.docTypeDisplay}</TableCell>
                    {showGrvColumns && (
                      <>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{formatShekels(item.invoiceSum)}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{formatShekels(item.grvSum)}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {typeof item.invoiceSum === "number" && typeof item.grvSum === "number"
                            ? formatShekels(item.invoiceSum - item.grvSum)
                            : "—"}
                        </TableCell>
                        <TableCell style={{ wordBreak: "break-word" }}>
                          {item.grvDocnos && item.grvDocnos.length > 0 ? item.grvDocnos.join(", ") : "—"}
                        </TableCell>
                      </>
                    )}
                    <TableCell>{item.source}</TableCell>
                    <TableCell>{dayjs(item.timestamp).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                      {item.fileUrl && (
                        <Tooltip title={viewFileLabel}>
                          <IconButton
                            component="a"
                            href={item.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <OpenInNewIcon fontSize="small" style={{ color: colors.theme.yaelBlue }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={item.handled ? unmarkHandledLabel : markHandledLabel}>
                        <IconButton
                          size="small"
                          onClick={() => onToggleHandled(item.id, !item.handled)}
                        >
                          {item.handled ? (
                            <FlagIcon fontSize="small" style={{ color: colors.theme.yaelGreen }} />
                          ) : (
                            <FlagOutlinedIcon fontSize="small" style={{ color: colors.theme.yaelOrange }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </StyledTable>
          </div>

          {hasMore && (
            <div style={{ paddingTop: 10, textAlign: "center", borderTop: "1px solid #f2f2f2" }}>
              <button
                onClick={() => setShowAll(!showAll)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: colors.theme.yaelBlue,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
              >
                {showAll
                  ? (t("SHOW_LESS") || "הצג פחות")
                  : `${t("SHOW_MORE") || "הצג עוד"} (${sortedItems.length - INITIAL_LIMIT}+)`}
              </button>
            </div>
          )}
        </>
      )}
    </TableContainer>
  );
};

// Pseudo doc-type tab: collects rows flagged as handled across every real doc
// type, and is the only tab that shows them.
const HANDLED_KEY = "HANDLED";
// Pseudo doc-type tab: open (not handled) חשבוניות מרכזות whose GRVs couldn't be
// linked - sum mismatch, GRV not found, etc. (grvStatus other than "matched").
// Those rows also stay under ALL and PINVOICE.
const GRV_MISMATCH_KEY = "GRV_MISMATCH";

const DOC_TYPES = [
  {
    key: "ALL",
    labelKey: "ALL",
    defaultLabel: "הכל",
    icon: FilterListIcon,
    accentColor: colors.theme.yaelBlue,
    lightBg: "rgba(0, 119, 182, 0.08)",
  },
  {
    key: "DOCUMENT_D",
    labelKey: "DOC_TYPE_DOCUMENT_D",
    defaultLabel: "תעודת משלוח",
    icon: LocalShippingOutlinedIcon,
    accentColor: colors.theme.yaelBlue,
    lightBg: "rgba(0, 119, 182, 0.08)",
  },
  {
    key: "PINVOICE",
    labelKey: "DOC_TYPE_PINVOICE",
    defaultLabel: "חשבונית ספק מרכזות",
    icon: ReceiptLongOutlinedIcon,
    accentColor: "#9C27B0",
    lightBg: "rgba(156, 39, 176, 0.08)",
  },
  {
    key: GRV_MISMATCH_KEY,
    labelKey: "GRV_MISMATCH_TAB",
    defaultLabel: "אי התאמה",
    icon: CompareArrowsIcon,
    accentColor: "#E65100",
    lightBg: "rgba(230, 81, 0, 0.08)",
  },
  {
    key: "YINVOICE",
    labelKey: "DOC_TYPE_YINVOICE",
    defaultLabel: "חשבונית ספק",
    icon: ReceiptOutlinedIcon,
    accentColor: colors.theme.yaelGreen,
    lightBg: "rgba(0, 167, 227, 0.08)",
  },
  {
    key: "PORDER",
    labelKey: "DOC_TYPE_PORDER",
    defaultLabel: "הזמנת רכש",
    icon: ShoppingCartOutlinedIcon,
    accentColor: "#009688",
    lightBg: "rgba(0, 150, 136, 0.08)",
  },
  {
    key: HANDLED_KEY,
    labelKey: "HANDLED_TAB",
    defaultLabel: "טופל",
    icon: TaskAltIcon,
    accentColor: colors.theme.yaelGrey,
    lightBg: "rgba(0, 0, 0, 0.05)",
  },
];

interface Props {
  billbotErrorBreakdown?: ErrorRecord[];
  priorityErrorBreakdown?: ErrorRecord[];
  onToggleHandled: (transactionId: string, handled: boolean) => void;
}

const ErrorBreakdownSection: React.FC<Props> = ({
  billbotErrorBreakdown,
  priorityErrorBreakdown,
  onToggleHandled,
}) => {
  const { t } = useTranslation();
  const [selectedDocType, setSelectedDocType] = useState<string>("ALL");

  const messageLabel = t("ERROR_MESSAGE") || "Error Message";
  const docTypeLabel = t("DOCUMENT_TYPE_COLUMN") || "Document Type";
  const dateLabel = t("ERROR_DATE_TIME") || "Date/Time";
  const sourceLabel = t("ERROR_SOURCE") || "Source";
  const viewFileLabel = t("VIEW_FILE") || "View file";
  const emptyLabel = t("NO_ERRORS_FOUND") || "No errors found";
  const actionsLabel = t("ACTIONS") || "Actions";
  const markHandledLabel = t("MARK_HANDLED_TOOLTIP") || "סמן כטופל";
  const unmarkHandledLabel = t("UNMARK_HANDLED_TOOLTIP") || "בטל סימון טופל";
  const priorityLabel = t("PRIORITY_ERROR_BREAKDOWN") || "Priority ERP Errors";
  const billbotLabel = t("BILLBOT_ERROR_BREAKDOWN") || "BillBot Errors";

  const getItems = (
    breakdown: ErrorRecord[] | undefined,
    docType: string
  ): ErrorRecord[] => {
    // Defensive: backend hasn't shipped the new flat-array contract yet (see BE_CHANGES.md #4),
    // so a legacy aggregated object can still arrive here at runtime despite the FE type.
    if (!Array.isArray(breakdown)) return [];
    // Handled rows leave the error tabs entirely and live only under HANDLED.
    if (docType === HANDLED_KEY) return breakdown.filter((item) => item.handled);
    const open = breakdown.filter((item) => !item.handled);
    if (docType === "ALL") return open;
    if (docType === GRV_MISMATCH_KEY) {
      return open.filter((item) => item.grvStatus && item.grvStatus !== "matched");
    }
    return open.filter((item) => item.docType === docType);
  };

  const getDocTypeLabel = (docType: string) => {
    const dt = DOC_TYPES.find((d) => d.key === docType);
    if (!dt) return docType;
    return t(dt.labelKey) || dt.defaultLabel;
  };

  const getDocTypeTotalErrors = (docType: string) => {
    return (
      getItems(priorityErrorBreakdown, docType).length +
      getItems(billbotErrorBreakdown, docType).length
    );
  };

  useEffect(() => {
    if (selectedDocType !== "ALL" && getDocTypeTotalErrors(selectedDocType) === 0) {
      setSelectedDocType("ALL");
    }
  }, [priorityErrorBreakdown, billbotErrorBreakdown, selectedDocType]);

  const visibleDocTypes = DOC_TYPES.filter((dt) => {
    if (dt.key === "ALL") return true;
    return getDocTypeTotalErrors(dt.key) > 0;
  });

  const currentPriorityItems = getItems(priorityErrorBreakdown, selectedDocType);
  const currentBillbotItems = getItems(billbotErrorBreakdown, selectedDocType);
  const mergedItems: MergedErrorRecord[] = [
    ...currentPriorityItems.map((item) => ({
      ...item,
      source: priorityLabel,
      docTypeDisplay: getDocTypeLabel(item.docType),
    })),
    ...currentBillbotItems.map((item) => ({
      ...item,
      source: billbotLabel,
      docTypeDisplay: getDocTypeLabel(item.docType),
    })),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, fontSize: "0.9rem", color: colors.theme.yaelDark, marginInlineEnd: 4 }}>
          {t("SELECT_DOCUMENT_TYPE") || "סוג מסמך"}:
        </span>
        {visibleDocTypes.map((dt) => {
          const isSelected = selectedDocType === dt.key;
          const totalErr = getDocTypeTotalErrors(dt.key);
          const label = t(dt.labelKey) || dt.defaultLabel;
          const IconComponent = dt.icon;

          return (
            <button
              key={dt.key}
              onClick={() => setSelectedDocType(dt.key)}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: isSelected
                  ? `1.5px solid ${dt.accentColor}`
                  : `1.5px solid ${dt.accentColor}33`,
                backgroundColor: isSelected ? dt.accentColor : dt.lightBg,
                color: isSelected ? "white" : colors.theme.yaelDark,
                fontWeight: isSelected ? 600 : 500,
                fontSize: "0.85rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.2s ease",
              }}
            >
              <IconComponent style={{ fontSize: 18, color: isSelected ? "white" : dt.accentColor }} />
              <span>{label}</span>
              {totalErr > 0 && (
                <span
                  style={{
                    backgroundColor: isSelected ? "white" : dt.accentColor,
                    color: isSelected ? dt.accentColor : "white",
                    borderRadius: "10px",
                    padding: "2px 6px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {totalErr}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <ChartsGrid>
        <ErrorBreakdownTable
          title={t("ERROR_BREAKDOWN_TITLE") || "Errors"}
          items={mergedItems}
          emptyLabel={emptyLabel}
          messageLabel={messageLabel}
          docTypeLabel={docTypeLabel}
          dateLabel={dateLabel}
          sourceLabel={sourceLabel}
          viewFileLabel={viewFileLabel}
          actionsLabel={actionsLabel}
          markHandledLabel={markHandledLabel}
          unmarkHandledLabel={unmarkHandledLabel}
          onToggleHandled={onToggleHandled}
          showGrvColumns={selectedDocType === GRV_MISMATCH_KEY}
        />
      </ChartsGrid>
    </div>
  );
};

export default ErrorBreakdownSection;

